import { useEffect } from "react";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import Translate from "./Translate";

const Modal = ({
  isOpen,
  onClose,
  title,
  height = 50,
  children,
  size = "md",
  loading,
}) => {
  const clipPolygon =
    "[clip-path:polygon(0.9em_0%,_calc(100%_-_0.9em)_0%,_100%_0.9em,_100%_calc(100%_-_0.9em),_calc(100%_-_0.9em)_100%,_0.9em_100%,_0%_calc(100%_-_0.9em),_0%_0.9em)]";

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent background scroll
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Size variants
  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };

  const heightClasses = {
    10: "h-[10vh]",
    20: "h-[20vh]",
    26: "h-[26vh]",
    30: "h-[30vh]",
    34: "h-[34vh]",
    40: "h-[40vh]",
    41: "h-[41vh]",
    42: "h-[42vh]",
    43: "h-[43vh]",
    47: "h-[47vh]",
    50: "h-[50vh]",
    60: "h-[60vh]",
    63: "h-[63vh]",
    65: "h-[65vh]",
    68: "h-[68vh]",
    70: "h-[70vh]",
    80: "h-[80vh]",
    90: "h-[90vh]",
    100: "h-[100vh]",
  };

  return (
 <AnimatePresence>
  {isOpen && (
    <div className="fixed inset-0 z-[105] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
  <motion.div
  initial={{ opacity: 0, scale: 0.9, y: 20 }}
  animate={{ opacity: 1, scale: 1, y: 0 }}
  exit={{ opacity: 0, scale: 0.95, y: 10 }}
  transition={{
    duration: 0.3,
    ease: [0.4, 0.0, 0.2, 1],
  }}
  className={`
    relative
    w-[500px]
    max-h-[90vh]
    overflow-hidden
    rounded-3xl
    bg-transparent
    before:content-['']
    before:absolute
    before:inset-0
    before:p-px
    before:rounded-3xl
    before:[background:linear-gradient(90deg,rgba(196,39,224,1)_0%,rgba(132,74,255,1)_47%,rgba(18,169,255,1)_100%)]
    before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)]
    before:[-webkit-mask-composite:xor]
    before:[mask-composite:exclude]
    before:z-10
    before:pointer-events-none
    bg-[#1A062A]
    ${heightClasses[height]} ${sizeClasses[size]}
  `}
  onClick={(e) => e.stopPropagation()}
>
  

        <div
          className={`
            absolute inset-[1px]
            rounded-3xl
            bg-[#1C1C1C]
            bg-opacity-90
            overflow-y-auto
            z-20 bg-[#1A062A]
          `}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between p-6 border-b border-white/20 rounded-t-3xl">
              <h2 className="text-xl font-semibold text-white">
                <Translate>{title}</Translate>
              </h2>
          {onClose && !loading && (
  <div className="w-[36px] h-[36px] rounded-lg cursor-pointer relative bg-[linear-gradient(90deg,rgba(196,39,224,1)_0%,rgba(132,74,255,1)_47%,rgba(18,169,255,1)_100%)]">
    <button
      className="absolute inset-1 bg-[#1C1C1C] bg-opacity-70 rounded-lg flex items-center justify-center cursor-pointer hover:bg-opacity-90 transition-all duration-200"
      onClick={onClose}
      aria-label="Close"
    >
      <Icon icon="mdi:times" className="text-white w-4 h-4" />
    </button>
  </div>
)}

            </div>
          )}

          {/* Body */}
          <div
            className={`xl:p-6 lg:p-6 p-4 overflow-y-auto max-h-[calc(${height}vh-120px)] z-[105]`}
          >
            {children}
          </div>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>

  );
};

export default Modal;
